import { supabase } from '@/integrations/supabase/client';

// HubSpot API configuration
export interface HubSpotConfig {
  portal_id?: string;
  api_key_encrypted?: string;
  oauth_token_encrypted?: string;
  refresh_token_encrypted?: string;
  webhook_secret?: string;
  sync_enabled: boolean;
  sync_schedule?: {
    frequency: 'manual' | 'hourly' | 'daily' | 'weekly';
    time?: string;
    dayOfWeek?: number;
  };
}

// HubSpot API client wrapper
export class HubSpotClient {
  private apiKey: string | null = null;
  private baseUrl = 'https://api.hubapi.com';

  async initialize() {
    // Fetch config from database
    const { data: config } = await supabase
      .from('hubspot_config')
      .select('*')
      .single();

    if (config?.api_key_encrypted) {
      // In production, decrypt the API key
      // For now, we'll use it directly (should be encrypted in real implementation)
      this.apiKey = config.api_key_encrypted;
    }

    return this;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    if (!this.apiKey) {
      throw new Error('HubSpot API key not configured');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `HubSpot API error: ${response.status}`);
    }

    return response.json();
  }

  // Contact methods
  async getContacts(limit = 100, after?: string) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      properties: 'email,firstname,lastname,phone,company,jobtitle',
    });
    
    if (after) {
      params.append('after', after);
    }

    return this.makeRequest(`/crm/v3/objects/contacts?${params}`);
  }

  async getContactById(contactId: string) {
    return this.makeRequest(`/crm/v3/objects/contacts/${contactId}`);
  }

  async createContact(properties: Record<string, any>) {
    return this.makeRequest('/crm/v3/objects/contacts', {
      method: 'POST',
      body: JSON.stringify({ properties }),
    });
  }

  async updateContact(contactId: string, properties: Record<string, any>) {
    return this.makeRequest(`/crm/v3/objects/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify({ properties }),
    });
  }

  // Company methods
  async getCompanies(limit = 100, after?: string) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      properties: 'name,domain,industry,city,state,country,numberofemployees',
    });
    
    if (after) {
      params.append('after', after);
    }

    return this.makeRequest(`/crm/v3/objects/companies?${params}`);
  }

  async getCompanyById(companyId: string) {
    return this.makeRequest(`/crm/v3/objects/companies/${companyId}`);
  }

  async createCompany(properties: Record<string, any>) {
    return this.makeRequest('/crm/v3/objects/companies', {
      method: 'POST',
      body: JSON.stringify({ properties }),
    });
  }

  async updateCompany(companyId: string, properties: Record<string, any>) {
    return this.makeRequest(`/crm/v3/objects/companies/${companyId}`, {
      method: 'PATCH',
      body: JSON.stringify({ properties }),
    });
  }

  // Deal methods
  async getDeals(limit = 100, after?: string) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      properties: 'dealname,amount,closedate,dealstage,pipeline',
    });
    
    if (after) {
      params.append('after', after);
    }

    return this.makeRequest(`/crm/v3/objects/deals?${params}`);
  }

  async getDealById(dealId: string) {
    return this.makeRequest(`/crm/v3/objects/deals/${dealId}`);
  }

  async createDeal(properties: Record<string, any>) {
    return this.makeRequest('/crm/v3/objects/deals', {
      method: 'POST',
      body: JSON.stringify({ properties }),
    });
  }

  async updateDeal(dealId: string, properties: Record<string, any>) {
    return this.makeRequest(`/crm/v3/objects/deals/${dealId}`, {
      method: 'PATCH',
      body: JSON.stringify({ properties }),
    });
  }

  // Engagement methods
  async getEngagements(limit = 100, after?: string) {
    const params = new URLSearchParams({
      limit: limit.toString(),
    });
    
    if (after) {
      params.append('after', after);
    }

    return this.makeRequest(`/crm/v3/objects/engagements?${params}`);
  }

  // Association methods
  async getAssociations(fromObjectType: string, fromObjectId: string, toObjectType: string) {
    return this.makeRequest(
      `/crm/v3/objects/${fromObjectType}/${fromObjectId}/associations/${toObjectType}`
    );
  }

  async createAssociation(
    fromObjectType: string,
    fromObjectId: string,
    toObjectType: string,
    toObjectId: string,
    associationType: string
  ) {
    return this.makeRequest(
      `/crm/v3/objects/${fromObjectType}/${fromObjectId}/associations/${toObjectType}/${toObjectId}/${associationType}`,
      { method: 'PUT' }
    );
  }

  // Webhook subscription methods
  async getWebhookSubscriptions(appId: string) {
    return this.makeRequest(`/webhooks/v3/${appId}/subscriptions`);
  }

  async createWebhookSubscription(appId: string, subscription: any) {
    return this.makeRequest(`/webhooks/v3/${appId}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  // Rate limit handling
  async checkRateLimit() {
    const response = await fetch(`${this.baseUrl}/integrations/v1/limit/daily`, {
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
      },
    });

    return response.json();
  }
}

// Singleton instance
let hubspotClient: HubSpotClient | null = null;

export async function getHubSpotClient(): Promise<HubSpotClient> {
  if (!hubspotClient) {
    hubspotClient = new HubSpotClient();
    await hubspotClient.initialize();
  }
  return hubspotClient;
}